import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  CircularProgress, 
  Checkbox, 
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem
} from '@mui/material';
import { 
  CheckIcon
} from '@heroicons/react/24/outline';
import { PANEL_COUNT_OPTIONS } from '../constants';
import { useItemTypes } from '../../../../config/itemTypes';
import { useSetTypes } from '../../../../config/setTypes';
import temporaryNumberingService from '../../../../services/temporaryNumberingService';
import { useMasterDropdown } from '../../../../hooks/useMasterDropdown';

interface ItemEntry {
  id: string;
  width: string;
  height: string;
  member: boolean;
  glass: boolean;
  bar: boolean;
}

interface Product {
  id: string;
  name?: string;
  label?: string;
  location?: string;
  productType?: string;
  color?: string;
  frameChecked?: boolean;
  frameCount?: number;
  panelChecked?: boolean;
  panelCount?: number;
  panelWindowChecked?: boolean;
  panelWindowCount?: number;
  netChecked?: boolean;
  netCount?: number;
  frameEntries?: ItemEntry[];
  panelEntries?: ItemEntry[];
  panelWindowEntries?: ItemEntry[];
  netEntries?: ItemEntry[];
  metadata?: {
    temporaryNumbers?: Record<string, any>;
  };
  // Additional properties from the main Product interface
  product_tracking_code?: string;
  order_id?: string;
  order_tracking_code?: string;
  customer_tracking_code?: string;
  type?: string;
  productNumber?: string;
  notes?: string;
  evaOrder?: string;
  specifications?: any;
  total_items?: number;
  completed_items?: number;
  unit_price?: number;
  quantity?: number;
}

interface ItemConfigurationPanelExcelProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  onComplete: (product: Product) => void;
  isEditMode?: boolean;
  onUpdate?: (product: Product) => void;
  isIncrementalMode?: boolean;
  onIncrementalComplete?: (product: Product) => void;
  // New props for auto order creation
  customerId?: string;
  siteId?: string;
  orderId?: string;
  orderData?: {
    amount?: string;
    paymentStatus?: string;
    priority?: string;
    dueDate?: string;
    notes?: string;
  };
  onOrderCreated?: (order: any) => void;
  onProductAdded?: (product: any) => void;
}


const ItemConfigurationPanelExcel: React.FC<ItemConfigurationPanelExcelProps> = ({
  isOpen,
  onClose,
  product,
  onComplete,
  isEditMode = false,
  onUpdate,
  isIncrementalMode = false,
  onIncrementalComplete,
  customerId,
  siteId,
  orderId,
  orderData,
  onOrderCreated,
  onProductAdded
}) => {
  // Fetch location options from master
  const { options: locationOptions, loading: locationLoading } = useMasterDropdown({
    masterType: 'locations',
    enabled: true,
  });

  // Fetch set types dynamically from product_masters table
  const { setTypes: productTypeOptions } = useSetTypes();
  
  // Fetch item types dynamically from item_masters table
  const { itemTypes: itemTypeOptions } = useItemTypes();
  
  // Helper function to get item type label by key (e.g., "frame" -> "Frame")
  const getItemTypeLabelByKey = (key: string): string => {
    // Explicit fallback mappings for ICM count keys
    const fallbackMap: Record<string, string> = {
      'frame': 'Frame',
      'panel': 'Panel',
      'panelwindow': 'Panel Window',
      'net': 'Net Fixing',
      'netfixing': 'Net Fixing'
    };
    
    const itemType = itemTypeOptions.find(it => {
      const normalizedKey = key.toLowerCase().replace(/\s+/g, '');
      const normalizedValue = it.value.toLowerCase().replace(/\s+/g, '');
      const normalizedLabel = it.label.toLowerCase().replace(/\s+/g, '');
      return normalizedValue === normalizedKey || normalizedLabel === normalizedKey;
    });
    
    // If found in itemTypeOptions, use it; otherwise check fallback map; otherwise return key
    if (itemType?.label) {
      return itemType.label;
    }
    
    const normalizedKey = key.toLowerCase().replace(/\s+/g, '');
    return fallbackMap[normalizedKey] || key;
  };

  const [formData, setFormData] = useState({
    label: '',
    location: '',
    productType: 'door',
    color: '',
    frameChecked: true, // Set frame as selected by default
    frameCount: 1,
    panelChecked: true,
    panelCount: 0,
    panelWindowChecked: true,
    panelWindowCount: 0,
    netChecked: true,
    netCount: 0,
    panelLaserCutting: false // Laser cutting setting for all panels
  });

  const [frameEntries, setFrameEntries] = useState<ItemEntry[]>([]);
  const [panelEntries, setPanelEntries] = useState<ItemEntry[]>([]);
  const [panelWindowEntries, setPanelWindowEntries] = useState<ItemEntry[]>([]);
  const [netEntries, setNetEntries] = useState<ItemEntry[]>([]);
  const [temporaryNumbers, setTemporaryNumbers] = useState<Record<string, any>>({});
  const [numbersGenerated, setNumbersGenerated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: boolean}>({});

  // Initialize form data when product changes
  useEffect(() => {
    if (product) {
      // Get configuration from metadata or use defaults
      const config = product.metadata?.configuration;
      setFormData({
        label: product.label || '',
        location: product.location || '',
        productType: product.type || 'door',
        color: product.color || '',
        frameChecked: config?.frameChecked !== undefined ? config.frameChecked : true,
        frameCount: config?.frameCount || 1,
        panelChecked: config?.panelCount > 0,
        panelCount: config?.panelCount || 0,
        panelWindowChecked: config?.panelWindowCount > 0,
        panelWindowCount: config?.panelWindowCount || 0,
        netChecked: config?.netCount > 0,
        netCount: config?.netCount || 0,
        panelLaserCutting: config?.panelLaserCutting || false
      });

      setFrameEntries(config?.frameEntries || []);
      setPanelEntries(config?.panelEntries || []);
      setPanelWindowEntries(config?.panelWindowEntries || []);
      setNetEntries(config?.netEntries || []);

      if (product.metadata?.temporaryNumbers) {
        setTemporaryNumbers(product.metadata.temporaryNumbers);
      }

      setNumbersGenerated(false);
    }
  }, [product, isEditMode]);

  // Generate numbers when counts change
  useEffect(() => {
    if (!numbersGenerated) {
      generateTemporaryNumbers();
    }
  }, [formData.frameCount, formData.panelCount, formData.panelWindowCount, formData.netCount, isEditMode, numbersGenerated]);

  // Adjust entries when counts change
  useEffect(() => {
    if (numbersGenerated) {
      adjustEntriesForCountChanges();
    }
  }, [formData.frameChecked, formData.frameCount, formData.panelCount, formData.panelWindowCount, formData.netCount, isEditMode]);

  const generateTemporaryNumbers = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await temporaryNumberingService.getLatestICMCounts();
      if (response.success) {
        const latestCounts = response.data;
        const generatedNumbers: Record<string, any> = {};

        // Generate numbers for each item type
        if (formData.frameChecked && formData.frameCount > 0) {
          const frameLabel = getItemTypeLabelByKey('frame');
          const baseCount = latestCounts[frameLabel] || 0;
          generatedNumbers[frameLabel] = {
            icm_count: baseCount,
            numbers: Array.from({ length: formData.frameCount }, (_, index) => {
              const number = `F${String(baseCount + index + 1).padStart(3, '0')}`;
              return number;
            })
          };
        }

        if (formData.panelChecked && formData.panelCount > 0) {
          const panelLabel = getItemTypeLabelByKey('panel');
          const baseCount = latestCounts[panelLabel] || 0;
          generatedNumbers[panelLabel] = {
            icm_count: baseCount,
            numbers: Array.from({ length: formData.panelCount }, (_, index) => {
              const number = `P${String(baseCount + index + 1).padStart(3, '0')}`;
              return number;
            })
          };
        }

        if (formData.panelWindowChecked && formData.panelWindowCount > 0) {
          const panelWindowLabel = getItemTypeLabelByKey('panelWindow');
          const baseCount = latestCounts[panelWindowLabel] || 0;
          generatedNumbers[panelWindowLabel] = {
            icm_count: baseCount,
            numbers: Array.from({ length: formData.panelWindowCount }, (_, index) => {
              const number = `PW${String(baseCount + index + 1).padStart(3, '0')}`;
              return number;
            })
          };
        }

        if (formData.netChecked && formData.netCount > 0) {
          const netLabel = getItemTypeLabelByKey('net');
          const baseCount = latestCounts[netLabel] || 0;
          generatedNumbers[netLabel] = {
            icm_count: baseCount,
            numbers: Array.from({ length: formData.netCount }, (_, index) => {
              const number = `N${String(baseCount + index + 1).padStart(3, '0')}`;
              return number;
            })
          };
        }

        setTemporaryNumbers(generatedNumbers);

        // Update entries with generated numbers
        if (formData.frameChecked && formData.frameCount > 0) {
          const newEntries = Array.from({ length: formData.frameCount }, (_, index) => ({
            id: generatedNumbers[getItemTypeLabelByKey('frame')]?.numbers[index] || '',
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false
          }));
          setFrameEntries(newEntries);
        } else {
          setFrameEntries([]);
        }

        if (formData.panelChecked && formData.panelCount > 0) {
          const newEntries = Array.from({ length: formData.panelCount }, (_, index) => ({
            id: generatedNumbers[getItemTypeLabelByKey('panel')]?.numbers[index] || '',
            width: '',
            height: '',
            member: true, // Default to true for panels
            glass: true, // Default to true for panels
            bar: true // Default to true for panels
          }));
          setPanelEntries(newEntries);
        } else {
          setPanelEntries([]);
        }

        if (formData.panelWindowChecked && formData.panelWindowCount > 0) {
          const newEntries = Array.from({ length: formData.panelWindowCount }, (_, index) => ({
            id: generatedNumbers[getItemTypeLabelByKey('panelWindow')]?.numbers[index] || '',
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false
          }));
          setPanelWindowEntries(newEntries);
        } else {
          setPanelWindowEntries([]);
        }

        if (formData.netChecked && formData.netCount > 0) {
          const newEntries = Array.from({ length: formData.netCount }, (_, index) => ({
            id: generatedNumbers[getItemTypeLabelByKey('net')]?.numbers[index] || '',
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false
          }));
          setNetEntries(newEntries);
        } else {
          setNetEntries([]);
        }

        setNumbersGenerated(true);
      }
    } catch (error) {
      console.error('Error generating temporary numbers:', error);
      setError('Failed to generate item numbers');
    } finally {
      setLoading(false);
    }
  };

  const adjustEntriesForCountChanges = async (currentFormData?: any) => {
    const formDataToUse = currentFormData || formData;
    // Get the base count from temporary numbers (which contains the latest ICM count)
    const getBaseCount = (itemType: string) => {
      const tempData = temporaryNumbers[itemType];
      return tempData ? tempData.icm_count : 0;
    };

    // For edit mode, we need to get fresh ICM counts for new items
    let freshICMCounts = null;
    if (isEditMode) {
      try {
        const response = await temporaryNumberingService.getLatestICMCounts();
        if (response.success) {
          freshICMCounts = response.data;
          console.log('Fresh ICM counts for edit mode:', freshICMCounts);
        }
      } catch (error) {
        console.error('Error getting fresh ICM counts:', error);
      }
    }

    // Adjust frame entries
    if (formDataToUse.frameChecked && formDataToUse.frameCount > 0) {
      const currentCount = frameEntries.length;
      if (formDataToUse.frameCount > currentCount) {
        // Add new entries starting from the base count + current count + 1
        let baseCount = getBaseCount(getItemTypeLabelByKey('frame'));
        
        // In edit mode, if we don't have existing frame entries, use fresh ICM count
        if (isEditMode && currentCount === 0 && freshICMCounts) {
          baseCount = freshICMCounts[getItemTypeLabelByKey('frame')] || 0;
        }
        
        const newEntries = Array.from({ length: formDataToUse.frameCount - currentCount }, (_, index) => {
          const fullName = `F${String(baseCount + currentCount + index + 1).padStart(3, '0')}`;
          return {
            id: fullName,
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false
          };
        });
        setFrameEntries(prev => [...prev, ...newEntries]);
      } else if (formDataToUse.frameCount < currentCount) {
        // Remove excess entries
        setFrameEntries(prev => prev.slice(0, formDataToUse.frameCount));
      }
    } else if (formDataToUse.frameChecked && formDataToUse.frameCount > 0 && frameEntries.length === 0) {
      // Handle case where frame is checked for the first time in edit mode
      let baseCount = getBaseCount(getItemTypeLabelByKey('frame'));
      
      if (isEditMode && freshICMCounts) {
        baseCount = freshICMCounts[getItemTypeLabelByKey('frame')] || 0;
      }
      
      const newEntries = Array.from({ length: formDataToUse.frameCount }, (_, index) => {
        const fullName = `F${String(baseCount + index + 1).padStart(3, '0')}`;
        return {
          id: fullName,
          width: '',
          height: '',
          member: false,
          glass: false,
          bar: false
        };
      });
      setFrameEntries(newEntries);
    } else {
      // Clear frame entries if unchecked
      setFrameEntries([]);
    }

    // Adjust panel entries
    const currentPanelCount = panelEntries.length;
    if (formDataToUse.panelCount > currentPanelCount) {
      let baseCount = getBaseCount(getItemTypeLabelByKey('panel'));
      
      // In edit mode, if we don't have existing panel entries, use fresh ICM count
      if (isEditMode && currentPanelCount === 0 && freshICMCounts) {
        baseCount = freshICMCounts[getItemTypeLabelByKey('panel')] || 0;
      }
      
      const newEntries = Array.from({ length: formDataToUse.panelCount - currentPanelCount }, (_, index) => {
        const fullName = `P${String(baseCount + currentPanelCount + index + 1).padStart(3, '0')}`;
        return {
          id: fullName,
          width: '',
          height: '',
          member: true, // Default to true for panels
          glass: true, // Default to true for panels
          bar: true // Default to true for panels
        };
      });
      setPanelEntries(prev => [...prev, ...newEntries]);
    } else if (formDataToUse.panelCount < currentPanelCount) {
      setPanelEntries(prev => prev.slice(0, formDataToUse.panelCount));
    }

    // Adjust panel window entries
    const currentPanelWindowCount = panelWindowEntries.length;
    if (formDataToUse.panelWindowCount > currentPanelWindowCount) {
      let baseCount = getBaseCount(getItemTypeLabelByKey('panelWindow'));
      
      // In edit mode, if we don't have existing panel window entries, use fresh ICM count
      if (isEditMode && currentPanelWindowCount === 0 && freshICMCounts) {
        baseCount = freshICMCounts[getItemTypeLabelByKey('panelWindow')] || 0;
      }
      
      const newEntries = Array.from({ length: formDataToUse.panelWindowCount - currentPanelWindowCount }, (_, index) => {
        const fullName = `PW${String(baseCount + currentPanelWindowCount + index + 1).padStart(3, '0')}`;
        return {
          id: fullName,
          width: '',
          height: '',
          member: false,
          glass: false,
          bar: false
        };
      });
      setPanelWindowEntries(prev => [...prev, ...newEntries]);
    } else if (formDataToUse.panelWindowCount < currentPanelWindowCount) {
      setPanelWindowEntries(prev => prev.slice(0, formDataToUse.panelWindowCount));
    }

    // Adjust net entries
    const currentNetCount = netEntries.length;
    if (formDataToUse.netCount > currentNetCount) {
      let baseCount = getBaseCount(getItemTypeLabelByKey('net'));
      
      // In edit mode, if we don't have existing net entries, use fresh ICM count
      if (isEditMode && currentNetCount === 0 && freshICMCounts) {
        baseCount = freshICMCounts[getItemTypeLabelByKey('net')] || 0;
      }
      
      const newEntries = Array.from({ length: formDataToUse.netCount - currentNetCount }, (_, index) => {
        const fullName = `N${String(baseCount + currentNetCount + index + 1).padStart(3, '0')}`;
        return {
          id: fullName,
          width: '',
          height: '',
          member: false,
          glass: false,
          bar: false
        };
      });
      setNetEntries(prev => [...prev, ...newEntries]);
    } else if (formDataToUse.netCount < currentNetCount) {
      setNetEntries(prev => prev.slice(0, formDataToUse.netCount));
    }
  };

  const handleInputChange = (field: string, value: any) => {
    const updatedFormData = { ...formData, [field]: value };
    setFormData(updatedFormData);
    
    // If this is a count field change, trigger entry regeneration
    if (['frameCount', 'panelCount', 'panelWindowCount', 'netCount', 'frameChecked'].includes(field)) {
      // Immediately trigger entry regeneration for count changes
      handleCountChange(updatedFormData);
    }
  };

  // New function specifically for handling count changes
  const handleCountChange = async (currentFormData: any) => {
    try {
      // Get fresh ICM counts
      let freshICMCounts = null;
      try {
        const response = await temporaryNumberingService.getLatestICMCounts();
        if (response.success) {
          freshICMCounts = response.data;
        }
      } catch (error) {
        console.error('Error getting fresh ICM counts:', error);
      }

      // Get the base count from temporary numbers (which contains the latest ICM count)
      const getBaseCount = (itemType: string) => {
        const tempData = temporaryNumbers[itemType];
        return tempData ? tempData.icm_count : 0;
      };

      // Adjust frame entries
      if (currentFormData.frameChecked && currentFormData.frameCount > 0) {
        const currentCount = frameEntries.length;
        if (currentFormData.frameCount > currentCount) {
          // Add new entries starting from the base count + current count + 1
          let baseCount = getBaseCount(getItemTypeLabelByKey('frame'));
          
          // In edit mode, if we don't have existing frame entries, use fresh ICM count
          if (isEditMode && currentCount === 0 && freshICMCounts) {
            baseCount = freshICMCounts[getItemTypeLabelByKey('frame')] || 0;
          }
          
          const newEntries = Array.from({ length: currentFormData.frameCount - currentCount }, (_, index) => {
            const fullName = `F${String(baseCount + currentCount + index + 1).padStart(3, '0')}`;
            return {
              id: fullName,
              width: '',
              height: '',
              member: false,
              glass: false,
              bar: false
            };
          });
          setFrameEntries(prev => [...prev, ...newEntries]);
        } else if (currentFormData.frameCount < currentCount) {
          // Remove excess entries
          setFrameEntries(prev => prev.slice(0, currentFormData.frameCount));
        }
      } else if (currentFormData.frameChecked && currentFormData.frameCount > 0 && frameEntries.length === 0) {
        // Handle case where frame is checked for the first time in edit mode
        let baseCount = getBaseCount(getItemTypeLabelByKey('frame'));
        
        if (isEditMode && freshICMCounts) {
          baseCount = freshICMCounts[getItemTypeLabelByKey('frame')] || 0;
        }
        
        const newEntries = Array.from({ length: currentFormData.frameCount }, (_, index) => {
          const fullName = `F${String(baseCount + index + 1).padStart(3, '0')}`;
          return {
            id: fullName,
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false
          };
        });
        setFrameEntries(newEntries);
      } else {
        // Clear frame entries if unchecked
        setFrameEntries([]);
      }

      // Adjust panel entries
      const currentPanelCount = panelEntries.length;
      if (currentFormData.panelCount > currentPanelCount) {
        let baseCount = getBaseCount(getItemTypeLabelByKey('panel'));
        
        // In edit mode, if we don't have existing panel entries, use fresh ICM count
        if (isEditMode && currentPanelCount === 0 && freshICMCounts) {
          baseCount = freshICMCounts[getItemTypeLabelByKey('panel')] || 0;
        }
        
        const newEntries = Array.from({ length: currentFormData.panelCount - currentPanelCount }, (_, index) => {
          const fullName = `P${String(baseCount + currentPanelCount + index + 1).padStart(3, '0')}`;
          return {
            id: fullName,
            width: '',
            height: '',
            member: true, // Default to true for panels
            glass: true, // Default to true for panels
            bar: true // Default to true for panels
          };
        });
        setPanelEntries(prev => [...prev, ...newEntries]);
      } else if (currentFormData.panelCount < currentPanelCount) {
        setPanelEntries(prev => prev.slice(0, currentFormData.panelCount));
      }

      // Adjust panel window entries
      const currentPanelWindowCount = panelWindowEntries.length;
      if (currentFormData.panelWindowCount > currentPanelWindowCount) {
        let baseCount = getBaseCount(getItemTypeLabelByKey('panelWindow'));
        
        // In edit mode, if we don't have existing panel window entries, use fresh ICM count
        if (isEditMode && currentPanelWindowCount === 0 && freshICMCounts) {
          baseCount = freshICMCounts[getItemTypeLabelByKey('panelWindow')] || 0;
        }
        
        const newEntries = Array.from({ length: currentFormData.panelWindowCount - currentPanelWindowCount }, (_, index) => {
          const fullName = `PW${String(baseCount + currentPanelWindowCount + index + 1).padStart(3, '0')}`;
          return {
            id: fullName,
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false
          };
        });
        setPanelWindowEntries(prev => [...prev, ...newEntries]);
      } else if (currentFormData.panelWindowCount < currentPanelWindowCount) {
        setPanelWindowEntries(prev => prev.slice(0, currentFormData.panelWindowCount));
      }

      // Adjust net entries
      const currentNetCount = netEntries.length;
      if (currentFormData.netCount > currentNetCount) {
        let baseCount = getBaseCount(getItemTypeLabelByKey('net'));
        
        // In edit mode, if we don't have existing net entries, use fresh ICM count
        if (isEditMode && currentNetCount === 0 && freshICMCounts) {
          baseCount = freshICMCounts[getItemTypeLabelByKey('net')] || 0;
        }
        
        const newEntries = Array.from({ length: currentFormData.netCount - currentNetCount }, (_, index) => {
          const fullName = `N${String(baseCount + currentNetCount + index + 1).padStart(3, '0')}`;
          return {
            id: fullName,
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false
          };
        });
        setNetEntries(prev => [...prev, ...newEntries]);
      } else if (currentFormData.netCount < currentNetCount) {
        setNetEntries(prev => prev.slice(0, currentFormData.netCount));
      }
    } catch (error) {
      console.error('Error handling count change:', error);
    }
  };

  const handleEntryChange = (type: string, index: number, field: string, value: any) => {
    const updateEntries = (entries: ItemEntry[], setEntries: React.Dispatch<React.SetStateAction<ItemEntry[]>>) => {
      const newEntries = [...entries];
      
      // Validate width and height as integers
      if (field === 'width' || field === 'height') {
        // Only allow integers
        const intValue = parseInt(value);
        if (isNaN(intValue) || intValue <= 0) {
          // Mark as invalid but still allow typing
          setValidationErrors(prev => ({
            ...prev,
            [`${type}-${index}-${field}`]: true
          }));
        } else {
          // Clear validation error
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`${type}-${index}-${field}`];
            return newErrors;
          });
          value = intValue.toString();
        }
      }
      
      newEntries[index] = { ...newEntries[index], [field]: value };
      setEntries(newEntries);
    };

    switch (type) {
      case 'frame':
        updateEntries(frameEntries, setFrameEntries);
        break;
      case 'panel':
        updateEntries(panelEntries, setPanelEntries);
        break;
      case 'panelWindow':
        updateEntries(panelWindowEntries, setPanelWindowEntries);
        break;
      case 'net':
        updateEntries(netEntries, setNetEntries);
        break;
    }
  };

  // Validation function to check if all required fields are filled
  const validateForm = () => {
    const errors: {[key: string]: boolean} = {};
    let isValid = true;

    // Check all entries for width and height
    const allEntries = [
      ...frameEntries.map((entry, index) => ({ entry, type: 'frame', index })),
      ...panelEntries.map((entry, index) => ({ entry, type: 'panel', index })),
      ...panelWindowEntries.map((entry, index) => ({ entry, type: 'panelWindow', index })),
      ...netEntries.map((entry, index) => ({ entry, type: 'net', index }))
    ];

    allEntries.forEach(({ entry, type, index }) => {
      const width = entry.width ? parseInt(entry.width) : 0;
      const height = entry.height ? parseInt(entry.height) : 0;
      
      if (isNaN(width) || width <= 0 || entry.width === '') {
        errors[`${type}-${index}-width`] = true;
        isValid = false;
      }
      
      if (isNaN(height) || height <= 0 || entry.height === '') {
        errors[`${type}-${index}-height`] = true;
        isValid = false;
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  // Check if form is valid for submit button
  const isFormValid = () => {
    // Check if all entries have valid width and height
    const allEntries = [
      ...frameEntries,
      ...panelEntries,
      ...panelWindowEntries,
      ...netEntries
    ];

    return allEntries.every(entry => {
      const width = entry.width ? parseInt(entry.width) : 0;
      const height = entry.height ? parseInt(entry.height) : 0;
      return !isNaN(width) && width > 0 && !isNaN(height) && height > 0 && entry.width !== '' && entry.height !== '';
    });
  };

  const handleSubmit = () => {
    // Validate form before submitting
    if (!validateForm()) {
      setError('Please fill in all required fields with valid values');
      return;
    }

    // Calculate total items count from all entries
    const totalItemsCount = frameEntries.length + panelEntries.length + 
                           panelWindowEntries.length + netEntries.length;

    // Calculate completed items count - preserve existing completed items if editing
    const completedItemsCount = isEditMode ? 
      (product?.completed_items || 0) : 
      0; // For new products, start with 0 completed items

    const updatedProduct: Product = {
      ...product!,
      ...formData,
      frameEntries,
      panelEntries,
      panelWindowEntries,
      netEntries,
      total_items: totalItemsCount,
      completed_items: completedItemsCount,
      metadata: {
        ...product?.metadata,
        temporaryNumbers
      }
    };

    console.log('🔄 ItemConfigurationPanelExcel - Updated Product:', {
      id: updatedProduct.id,
      name: updatedProduct.name,
      type: updatedProduct.type,
      label: updatedProduct.label,
      location: updatedProduct.location,
      total_items: updatedProduct.total_items,
      completed_items: updatedProduct.completed_items,
      frame_count: frameEntries.length,
      panel_count: panelEntries.length,
      panel_window_count: panelWindowEntries.length,
      net_count: netEntries.length
    });

    if (isEditMode && onUpdate) {
      onUpdate(updatedProduct);
    } else {
      onComplete(updatedProduct);
    }
  };

  const getAllEntries = () => {
    const allEntries: Array<{ type: string; entry: ItemEntry; index: number }> = [];
    
    frameEntries.forEach((entry, index) => {
      allEntries.push({ type: 'Frame', entry, index });
    });
    
    panelEntries.forEach((entry, index) => {
      allEntries.push({ type: 'Panel', entry, index });
    });
    
    panelWindowEntries.forEach((entry, index) => {
      allEntries.push({ type: 'Panel Window', entry, index });
    });
    
    netEntries.forEach((entry, index) => {
      allEntries.push({ type: 'Net', entry, index });
    });
    
    return allEntries;
  };


  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

      <div
        className={`absolute right-0 top-0 h-full w-[30%] min-w-[400px] bg-white shadow-xl transform transition-transform duration-500 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-1 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900 truncate pr-2">
              {formData.label && formData.location 
                ? `${formData.label} - ${formData.location}` 
                : `${isEditMode ? 'Edit' : 'Configure'} Items - ${product?.name || 'Product'}`}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 p-1" style={{ maxHeight: 'calc(100vh - 100px)', overflow: 'hidden' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Sequential Fields - Responsive */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 1 }}>
              <TextField
                label="Label"
                value={formData.label}
                onChange={(e) => handleInputChange('label', e.target.value)}
                sx={{ flex: 1 }}
                autoFocus
              />
              
              <TextField
                select
                label="Location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                sx={{ flex: 1 }}
                disabled={locationLoading}
              >
                <MenuItem value="">Select Location</MenuItem>
                {locationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              
              <TextField
                select
                label="Product Type"
                value={formData.productType}
                onChange={(e) => handleInputChange('productType', e.target.value)}
                sx={{ flex: 1 }}
              >
                {productTypeOptions.map((setType) => (
                  <MenuItem key={setType.value} value={setType.value}>
                    {setType.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Counts and Checkboxes - Responsive */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 0.5, sm: 1 }, 
              mb: 1 
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.frameChecked}
                    onChange={(e) => handleInputChange('frameChecked', e.target.checked)}
                  />
                }
                label="Frame"
              />
              
              <TextField
                select
                label="Panel"
                value={formData.panelCount}
                onChange={(e) => handleInputChange('panelCount', parseInt(e.target.value))}
                size="small"
                sx={{ minWidth: 120 }}
              >
                {PANEL_COUNT_OPTIONS.map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </TextField>
              
              {formData.panelCount > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.panelLaserCutting || false}
                      onChange={(e) => handleInputChange('panelLaserCutting', e.target.checked)}
                      size="small"
                    />
                  }
                  label="Laser Cutting"
                  sx={{ 
                    fontSize: '0.875rem',
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem'
                    }
                  }}
                />
              )}
              
              <TextField
                select
                label="Panel Window"
                value={formData.panelWindowCount}
                onChange={(e) => handleInputChange('panelWindowCount', parseInt(e.target.value))}
                size="small"
                disabled={formData.panelWindowCount > formData.panelCount}
                sx={{ minWidth: 150 }}
              >
                {Array.from({ length: formData.panelCount }, (_, i) => i + 1).map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </TextField>
              
              <TextField
                select
                label="Net"
                value={formData.netCount}
                onChange={(e) => handleInputChange('netCount', parseInt(e.target.value))}
                size="small"
                disabled={formData.netCount > formData.panelWindowCount}
                sx={{ minWidth: 120 }}
              >
                {Array.from({ length: formData.panelWindowCount }, (_, i) => i + 1).map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Frame Entries */}
            {formData.frameChecked && formData.frameCount > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Frame</Typography>
                  <Box sx={{ 
                    px: 1, 
                    py: 0.25, 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    borderRadius: 0.5, 
                    fontSize: '0.7rem' 
                  }}>
                    {frameEntries.length}
                  </Box>
                </Box>
                <TableContainer component={Paper} sx={{ mb: 1 }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '2px 4px' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '100px', minWidth: '100px' }}>Item #</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '60px', minWidth: '60px' }}>Width</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '60px', minWidth: '60px' }}>Height</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>M</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>B</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>G</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {frameEntries.map((entry, index) => (
                        <TableRow key={`frame-${index}`} hover>
                          <TableCell>
                                    <TextField
                                      value={entry.id}
                                      size="small"
                                      disabled
                                      sx={{
                                        width: '100%',
                                        '& .MuiInputBase-input': {
                                          fontSize: '12px',
                                          fontFamily: 'monospace',
                                          fontWeight: 'bold',
                                          color: '#1976d2',
                                          backgroundColor: '#e3f2fd',
                                          padding: '2px 4px'
                                        }
                                      }}
                                    />
                          </TableCell>
                          <TableCell>
                                    <TextField
                                      value={entry.width}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        // Only allow numeric input (including empty string and partial numbers)
                                        if (value === '' || /^\d*$/.test(value)) {
                                          handleEntryChange('frame', index, 'width', value);
                                        }
                                      }}
                                      size="small"
                                      type="text"
                                      inputProps={{ min: 1, step: 1 }}
                                      error={validationErrors[`frame-${index}-width`]}
                                      sx={{
                                        width: '100%',
                                        '& .MuiInputBase-input': {
                                          fontSize: '12px',
                                          fontFamily: 'monospace',
                                          fontWeight: 'bold',
                                          padding: '2px 4px',
                                          backgroundColor: validationErrors[`frame-${index}-width`] ? '#ffebee' : 'transparent'
                                        },
                                        '& .MuiOutlinedInput-root': {
                                          '& fieldset': {
                                            borderColor: validationErrors[`frame-${index}-width`] ? '#f44336' : undefined,
                                            borderWidth: validationErrors[`frame-${index}-width`] ? 2 : 1
                                          }
                                        }
                                      }}
                                    />
                          </TableCell>
                          <TableCell>
                                    <TextField
                                      value={entry.height}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        // Only allow numeric input (including empty string and partial numbers)
                                        if (value === '' || /^\d*$/.test(value)) {
                                          handleEntryChange('frame', index, 'height', value);
                                        }
                                      }}
                                      size="small"
                                      type="text"
                                      inputProps={{ min: 1, step: 1 }}
                                      error={validationErrors[`frame-${index}-height`]}
                                      sx={{
                                        width: '100%',
                                        '& .MuiInputBase-input': {
                                          fontSize: '12px',
                                          fontFamily: 'monospace',
                                          fontWeight: 'bold',
                                          padding: '2px 4px',
                                          backgroundColor: validationErrors[`frame-${index}-height`] ? '#ffebee' : 'transparent'
                                        },
                                        '& .MuiOutlinedInput-root': {
                                          '& fieldset': {
                                            borderColor: validationErrors[`frame-${index}-height`] ? '#f44336' : undefined,
                                            borderWidth: validationErrors[`frame-${index}-height`] ? 2 : 1
                                          }
                                        }
                                      }}
                                    />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.member || false}
                              onChange={(e) => handleEntryChange('frame', index, 'member', e.target.checked)}
                              disabled={true}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.bar || false}
                              onChange={(e) => handleEntryChange('frame', index, 'bar', e.target.checked)}
                              disabled={true}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.glass || false}
                              onChange={(e) => handleEntryChange('frame', index, 'glass', e.target.checked)}
                              disabled={true}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Panel Entries */}
            {formData.panelChecked && formData.panelCount > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Panel</Typography>
                  <Box sx={{ 
                    px: 1, 
                    py: 0.25, 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    borderRadius: 0.5, 
                    fontSize: '0.7rem' 
                  }}>
                    {panelEntries.length}
                  </Box>
                </Box>
                <TableContainer component={Paper} sx={{ mb: 1 }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '2px 4px' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '100px', minWidth: '100px' }}>Item #</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '60px', minWidth: '60px' }}>Width</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '60px', minWidth: '60px' }}>Height</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>M</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>B</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>G</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {panelEntries.map((entry, index) => (
                        <TableRow key={`panel-${index}`} hover>
                          <TableCell>
                                    <TextField
                                      value={entry.id}
                                      size="small"
                                      disabled
                                      sx={{
                                        width: '100%',
                                        '& .MuiInputBase-input': {
                                          fontSize: '12px',
                                          fontFamily: 'monospace',
                                          fontWeight: 'bold',
                                          color: '#1976d2',
                                          backgroundColor: '#e3f2fd',
                                          padding: '2px 4px'
                                        }
                                      }}
                                    />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={entry.width}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numeric input (including empty string and partial numbers)
                                if (value === '' || /^\d*$/.test(value)) {
                                  handleEntryChange('panel', index, 'width', value);
                                }
                              }}
                              size="small"
                              type="text"
                              inputProps={{ min: 1, step: 1 }}
                              error={validationErrors[`panel-${index}-width`]}
                              sx={{ 
                                minWidth: '80px',
                                '& .MuiInputBase-input': {
                                  fontSize: '16px',
                                  fontFamily: 'monospace',
                                  fontWeight: 'bold',
                                  backgroundColor: validationErrors[`panel-${index}-width`] ? '#ffebee' : 'transparent'
                                },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: validationErrors[`panel-${index}-width`] ? '#f44336' : undefined,
                                    borderWidth: validationErrors[`panel-${index}-width`] ? 2 : 1
                                  }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={entry.height}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numeric input (including empty string and partial numbers)
                                if (value === '' || /^\d*$/.test(value)) {
                                  handleEntryChange('panel', index, 'height', value);
                                }
                              }}
                              size="small"
                              type="text"
                              inputProps={{ min: 1, step: 1 }}
                              error={validationErrors[`panel-${index}-height`]}
                              sx={{ 
                                minWidth: '80px',
                                '& .MuiInputBase-input': {
                                  fontSize: '16px',
                                  fontFamily: 'monospace',
                                  fontWeight: 'bold',
                                  backgroundColor: validationErrors[`panel-${index}-height`] ? '#ffebee' : 'transparent'
                                },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: validationErrors[`panel-${index}-height`] ? '#f44336' : undefined,
                                    borderWidth: validationErrors[`panel-${index}-height`] ? 2 : 1
                                  }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.member || false}
                              onChange={(e) => handleEntryChange('panel', index, 'member', e.target.checked)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.bar || false}
                              onChange={(e) => handleEntryChange('panel', index, 'bar', e.target.checked)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.glass || false}
                              onChange={(e) => handleEntryChange('panel', index, 'glass', e.target.checked)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Panel Window Entries */}
            {formData.panelWindowChecked && formData.panelWindowCount > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Panel Window</Typography>
                  <Box sx={{ 
                    px: 1, 
                    py: 0.25, 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    borderRadius: 0.5, 
                    fontSize: '0.7rem' 
                  }}>
                    {panelWindowEntries.length}
                  </Box>
                </Box>
                <TableContainer component={Paper} sx={{ mb: 1 }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '2px 4px' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '100px', minWidth: '100px' }}>Item #</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '60px', minWidth: '60px' }}>Width</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '60px', minWidth: '60px' }}>Height</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>M</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>B</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>G</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {panelWindowEntries.map((entry, index) => (
                        <TableRow key={`panelWindow-${index}`} hover>
                          <TableCell>
                                    <TextField
                                      value={entry.id}
                                      size="small"
                                      disabled
                                      sx={{
                                        width: '100%',
                                        '& .MuiInputBase-input': {
                                          fontSize: '12px',
                                          fontFamily: 'monospace',
                                          fontWeight: 'bold',
                                          color: '#1976d2',
                                          backgroundColor: '#e3f2fd',
                                          padding: '2px 4px'
                                        }
                                      }}
                                    />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={entry.width}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numeric input (including empty string and partial numbers)
                                if (value === '' || /^\d*$/.test(value)) {
                                  handleEntryChange('panelWindow', index, 'width', value);
                                }
                              }}
                              size="small"
                              type="text"
                              inputProps={{ min: 1, step: 1 }}
                              error={validationErrors[`panelWindow-${index}-width`]}
                              sx={{ 
                                minWidth: '80px',
                                '& .MuiInputBase-input': {
                                  fontSize: '16px',
                                  fontFamily: 'monospace',
                                  fontWeight: 'bold',
                                  backgroundColor: validationErrors[`panelWindow-${index}-width`] ? '#ffebee' : 'transparent'
                                },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: validationErrors[`panelWindow-${index}-width`] ? '#f44336' : undefined,
                                    borderWidth: validationErrors[`panelWindow-${index}-width`] ? 2 : 1
                                  }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={entry.height}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numeric input (including empty string and partial numbers)
                                if (value === '' || /^\d*$/.test(value)) {
                                  handleEntryChange('panelWindow', index, 'height', value);
                                }
                              }}
                              size="small"
                              type="text"
                              inputProps={{ min: 1, step: 1 }}
                              error={validationErrors[`panelWindow-${index}-height`]}
                              sx={{ 
                                minWidth: '80px',
                                '& .MuiInputBase-input': {
                                  fontSize: '16px',
                                  fontFamily: 'monospace',
                                  fontWeight: 'bold',
                                  backgroundColor: validationErrors[`panelWindow-${index}-height`] ? '#ffebee' : 'transparent'
                                },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: validationErrors[`panelWindow-${index}-height`] ? '#f44336' : undefined,
                                    borderWidth: validationErrors[`panelWindow-${index}-height`] ? 2 : 1
                                  }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.member || false}
                              onChange={(e) => handleEntryChange('panelWindow', index, 'member', e.target.checked)}
                              disabled={true}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.bar || false}
                              onChange={(e) => handleEntryChange('panelWindow', index, 'bar', e.target.checked)}
                              disabled={true}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.glass || false}
                              onChange={(e) => handleEntryChange('panelWindow', index, 'glass', e.target.checked)}
                              disabled={true}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Net Entries */}
            {formData.netChecked && formData.netCount > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Net</Typography>
                  <Box sx={{ 
                    px: 1, 
                    py: 0.25, 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    borderRadius: 0.5, 
                    fontSize: '0.7rem' 
                  }}>
                    {netEntries.length}
                  </Box>
                </Box>
                <TableContainer component={Paper} sx={{ mb: 1 }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '2px 4px' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '100px', minWidth: '100px' }}>Item #</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '60px', minWidth: '60px' }}>Width</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '60px', minWidth: '60px' }}>Height</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>M</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>B</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '50px', minWidth: '50px', textAlign: 'center' }}>G</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {netEntries.map((entry, index) => (
                        <TableRow key={`net-${index}`} hover>
                          <TableCell>
                                    <TextField
                                      value={entry.id}
                                      size="small"
                                      disabled
                                      sx={{
                                        width: '100%',
                                        '& .MuiInputBase-input': {
                                          fontSize: '12px',
                                          fontFamily: 'monospace',
                                          fontWeight: 'bold',
                                          color: '#1976d2',
                                          backgroundColor: '#e3f2fd',
                                          padding: '2px 4px'
                                        }
                                      }}
                                    />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={entry.width}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numeric input (including empty string and partial numbers)
                                if (value === '' || /^\d*$/.test(value)) {
                                  handleEntryChange('net', index, 'width', value);
                                }
                              }}
                              size="small"
                              type="text"
                              inputProps={{ min: 1, step: 1 }}
                              error={validationErrors[`net-${index}-width`]}
                              sx={{ 
                                minWidth: '80px',
                                '& .MuiInputBase-input': {
                                  fontSize: '16px',
                                  fontFamily: 'monospace',
                                  fontWeight: 'bold',
                                  backgroundColor: validationErrors[`net-${index}-width`] ? '#ffebee' : 'transparent'
                                },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: validationErrors[`net-${index}-width`] ? '#f44336' : undefined,
                                    borderWidth: validationErrors[`net-${index}-width`] ? 2 : 1
                                  }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={entry.height}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numeric input (including empty string and partial numbers)
                                if (value === '' || /^\d*$/.test(value)) {
                                  handleEntryChange('net', index, 'height', value);
                                }
                              }}
                              size="small"
                              type="text"
                              inputProps={{ min: 1, step: 1 }}
                              error={validationErrors[`net-${index}-height`]}
                              sx={{ 
                                minWidth: '80px',
                                '& .MuiInputBase-input': {
                                  fontSize: '16px',
                                  fontFamily: 'monospace',
                                  fontWeight: 'bold',
                                  backgroundColor: validationErrors[`net-${index}-height`] ? '#ffebee' : 'transparent'
                                },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: validationErrors[`net-${index}-height`] ? '#f44336' : undefined,
                                    borderWidth: validationErrors[`net-${index}-height`] ? 2 : 1
                                  }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.member || false}
                              onChange={(e) => handleEntryChange('net', index, 'member', e.target.checked)}
                              disabled={true}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.bar || false}
                              onChange={(e) => handleEntryChange('net', index, 'bar', e.target.checked)}
                              disabled={true}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={entry.glass || false}
                              onChange={(e) => handleEntryChange('net', index, 'glass', e.target.checked)}
                              disabled={true}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </div>

          {/* Footer */}
          <div className="p-1 border-t border-gray-200">
            <Button
              variant="contained"
              fullWidth
              size="medium"
              onClick={handleSubmit}
              disabled={loading || !isFormValid()}
              startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon className="h-4 w-4" />}
              sx={{
                backgroundColor: !isFormValid() ? '#ccc' : undefined,
                '&:hover': {
                  backgroundColor: !isFormValid() ? '#ccc' : undefined
                }
              }}
            >
              {loading ? 'Processing...' : (isEditMode ? 'Update Product' : 'Add Product')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemConfigurationPanelExcel;
